// React
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from "react-router-dom";
import { useForm } from 'react-hook-form';
// ライブラリ
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
import CreatableSelect from 'react-select/creatable';
// コンポーネント
import { customStyles, customTheme } from '../../../lib/ReactSelectStyle';
// カスタムフック
import { useEditStateStorage } from '../../../hooks/useEditStateStorage';
// ユーティリティ
import { settings, menu } from '../../../constants';
import { validationSchema, environmentValidation } from '../../../utils/validationUtils';
import { successMessages, errorMessages } from '../../../utils/messagesUtils';
// スタイル
import '../../../lib/ReactModalStyle.css';
import sty from './Main.module.css';
// ステート
import { skillsheetState } from './state/skillsheetState';


const Projects = ({ years, months }) => {
    const navigate = useNavigate()
    const [isLoading, setIsLoading] = useState(true);

    Projects.propTypes = {
        years: PropTypes.array.isRequired,
        months: PropTypes.array.isRequired,
    };

    const defaultProjects = {
            projects: [
                {
                pc: [
                  {
                    pc_con: [{ name: "pc_con_1", value: "" }],
                    pc_type: [{ name: "pc_type_1", value: "" }]
                  }
                ],
                dev: [
                  {
                    dev_con: [{ name: "dev_con_1", value: "" }],
                    dev_type: [{ name: "dev_type_1", value: "" }]
                  }
                ],
                cloud: [
                  {
                    cloud_con: [{ name: "cloud_con_1", value: "" }],
                    cloud_type: [{ name: "cloud_type_1", value: "" }]
                  }
                ],
                client: [
                  {
                    client_con: [{ name: "client_con_1", value: "" }],
                    client_type: [{ name: "client_type_1", value: "" }]
                  }
                ],
                server: [
                  {
                    server_con: [{ name: "server_con_1", value: "" }],
                    server_type: [{ name: "server_type_1", value: "" }]
                  }
                ],
                network: [
                  {
                    network_con: [{ name: "network_con_1", value: "" }],
                    network_type: [{ name: "network_type_1", value: "" }]
                  }
                ],
                process: {
                    coding: false,
                    load_test: false,
                    operation: false,
                    unit_test: false,
                    system_test: false,
                    basic_design: false,
                    security_test: false,
                    detailed_design: false,
                    integration_test: false,
                    requirement_analysis: false
                },
                project_name: "",
                project_id: "",
                is_participating: "false",
                start_year: "",
                start_month: "",
                end_year: "",
                end_month: "",
                project_category: "",
                industry: "",
                role: "",
                scale: "",
                overview: "",
                created_at: "",
                updated_at: "",
              }
            ],
        }

    const [originalData, setOriginalData] = useState(null);

    // フォームのstateを更新
    const { register, formState: { errors }, trigger, reset, setError, clearErrors } = useForm({
        mode: 'onSubmit', // 保存時にバリデーションを実行
        reValidateMode: 'onSubmit', // 一度エラーが発生した後も、次の保存時にのみ再バリデーションを実行
        defaultValues: defaultProjects
    });

    const safeOriginalData = useMemo(() => 
        originalData ? JSON.parse(JSON.stringify(originalData)) : null, 
        [originalData]
    );

    const [forms, setForms] = useState([]);

    const { checkForEditData, clearStoredData } = useEditStateStorage(
        'projects', // ストレージキープレフィックス
        () => forms, // フォームデータを取得する関数
        (data) => {
            if (Array.isArray(data) && data.length > 0) {
                // データが配列の形式で保存されている場合、それをformsにセット
                setForms(data);

                // 表示状態をセット
                setVisibleStates(Array(data.length).fill(true));
            }
        },
        safeOriginalData, // オリジナルデータを取得する関数
    );


    // 各フォームタイトルの参照を保存するためのrefを作成
    const formRefs = useRef([]);

    // フォームタイトルの参照を設定するための関数
    const setFormRef = (index, element) => {
        formRefs.current[index] = element;
    };

    // フォーム自体を複製(フォーム内全ての項目とともに複製) →1つ目のフォーム、2つ目のフォーム...
    const addForm = () => {
        // フォームのクローン
        const newForm = { ...defaultProjects.projects[0] };
        const newForms = [...forms, newForm];
        // 新しいフォームの追加
        setForms(newForms);

        // 表示状態を更新し、新しいフォームだけを開く
        const updatedVisibleStates = forms.map(() => false); 
        // 新しいフォームだけ開く
        updatedVisibleStates.push(true); 
        setVisibleStates(updatedVisibleStates);

        // 新しいフォームまでスクロール
        setTimeout(() => {
            // 新しいフォームのインデックス
            const lastFormRef = formRefs.current[formRefs.current.length -1];
            if (lastFormRef) {
                // 新しいフォームまでスムーズにスクロール
                lastFormRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    };

    // SP版とPC版でDOMの切り替えをする
    const isSPDevice = UseIsSPDevice();

    // PJ参画期間の開始日が終了日よりも前の場合、バリデーションメッセージを表示
    const validateDateRange = (formIndex) => {
        const startYearMonth = parseInt(`${forms[formIndex].start_year}${String(forms[formIndex].start_month).padStart(2, '0')}`, 10);
        const endYearMonth = parseInt(`${forms[formIndex].end_year}${String(forms[formIndex].end_month).padStart(2, '0')}`, 10);

        if (forms[formIndex].start_year && forms[formIndex].start_month && forms[formIndex].end_year && forms[formIndex].end_month) { // 入力途中でバリデーションが発火しないように、全てのフォームを入力したら開始日と終了日を比較する
            if (startYearMonth > endYearMonth) {
                setError(`forms[${formIndex}].date_range`, {
                    type: "manual",
                    message: "開始日は終了日より前に設定してください",
                });
            } else { // 開始日が終了日よりも後(正常)の場合はエラーをクリア(エラーメッセージ非表示)
                clearErrors(`forms[${formIndex}].date_range`);
            }
        } else if (!forms[formIndex].start_year && !forms[formIndex].start_month && forms[formIndex].end_year && forms[formIndex].end_month) {
            setError(`forms[${formIndex}].date_range`, {
                type: "manual",
                message: "開始日を入力してください",
            });
        }
    };

    // formIndexに応じてそのフォームの期間の値が変更された際にvalidateDateRange関数を呼び出す
    useEffect(() => {
        forms.forEach((_, formIndex) => {
            validateDateRange(formIndex);
        });
    }, [forms]);

    // useEffectフックを使用してproject_nameの変更を監視
    useEffect(() => {
        forms.forEach((form, index) => {
            const projectNameElement = document.querySelector(`.form__tit-pj[data-index="${index}"]`);
            if (projectNameElement) {
                projectNameElement.textContent = `プロジェクト${index + 1}`;
            }
        });
    }, [forms]);

    // フォームのvalue更新
    const handleInputChange = (e, formIndex, name, isChecked) => {
        const newForms = [...forms];
        if(isChecked !== undefined) {
            newForms[formIndex].process[name] = isChecked;
        } else {
            newForms[formIndex][name] = e.target.value;
        }
        
        // 参画中ステータス
        if (name === 'is_participating' && e.target.value === 'true') {
            newForms[formIndex].end_year = '';
            newForms[formIndex].end_month = '';
        }
        setForms(newForms);
    };
    
    // フォーム削除時の処理
    const deleteForm = async (projectId, projectName, formIndex) => {
        const result = Swal.fire({
            icon: 'warning',
            html: `本当に <b>${projectName}</b> を<br>削除してもよろしいですか?`,
            showCancelButton: true,
            confirmButtonText: "削除",
            cancelButtonText: "キャンセル",
            customClass: {
                htmlContainer: 'large-text'
            }
        })

        if(!(await result).isConfirmed){ 
            return 
        }
        //check for skillsheet
        let projectIdFound = false;
        for(let box = 0; box < skillsheetState.forms.length; box++){
            let projectDataPerSkillsheet = skillsheetState.forms[box].input.project.project_data;
            // let selected_project_name = '';

            let skillsheet_name = skillsheetState.forms[box].input.skillsheet_name;
            
            projectIdFound = false;
            for(let pdps = 0; pdps < projectDataPerSkillsheet.length; pdps++){
                if(projectId === projectDataPerSkillsheet[pdps].project_id){
                    // selected_project_name = projectDataPerSkillsheet[pdps].selected_project_name;
                    projectIdFound = true;
                    break
                }
            }
            if(projectIdFound){
                await Swal.fire({
                    icon: 'info', 
                    html: `スキルシート <b>${skillsheet_name}</b> にて、<br>登録済みのため削除できません。`,
                    customClass: {
                        htmlContainer: 'large-text'
                    }
                });
                break;
            }
        }
        if(projectIdFound){
            return;
        }
        // DBに対象のプロジェクトがある場合
        if(projectId) {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/project/${projectId}`, {
                    method: "DELETE",
                    headers: {
                        "Accept": "application/json",
                        "Content-Type": "application/json", 
                    },
                    credentials: "include",
                })
                const result = await response.json();
                if(response.status === 200 && result.status === 'OK'){
                    // フォームの状態を更新して再レンダリング
                    setForms(prevForms => prevForms.filter((_, index) => index !== formIndex));
                    clearErrors()
                    fetchOptionsData()
                    clearStoredData();
                    Swal.fire({ icon: 'success', title: '削除完了' });
                } else {
                    if(response.status === 401){
                        navigate(menu.ENGINEER.SIGNIN.PATH)
                    } else {
                        throw new Error();
                    }
                }
            } catch(e) {
                await Swal.fire({
                    icon: 'error',
                    html: `${e.front_message || errorMessages.api.server_error}`,
                    customClass: {
                        htmlContainer: 'large-text'
                    }
                })
            }
        } else {
            //DBに対象のプロジェクトがない場合
            const newForms = forms.filter((_, i) => i !== formIndex);
            setForms(newForms);
            clearErrors()
            Swal.fire({
                icon: 'info',
                html: errorMessages.projects.not_found,
                customClass: {
                    htmlContainer: 'large-text'
                }
            });
        }
    }

    // システム/作業環境のバリデーションエラーをチェックする関数
    const checkEnvironmentErrors = (formIndex) => {
        const categories = ['server', 'client', 'dev', 'cloud', 'pc', 'network'];
        
        // いずれかのカテゴリでエラーがあり、かつエラーメッセージが存在すればtrueを返す
        return categories.some(category => {
            const categoryErrors = errors.forms?.[formIndex]?.[`${category}_range`];
            // 配列かつ中身があるかチェック、または単一のエラーオブジェクトでメッセージがあるかチェック
            return (Array.isArray(categoryErrors) && categoryErrors.some(err => err?.message)) || 
                (categoryErrors && categoryErrors.message);
        });
    };

    // フォーム送信時の処理
    const submitFormHandler = async (formIndex) => {
        const fieldsToValidate = [
            `start_year_${formIndex + 1}`,
            `start_month_${formIndex + 1}`,
            `end_year_${formIndex + 1}`, // 案件終了の場合のみバリデーション発火
            `end_month_${formIndex + 1}`, // 案件終了の場合のみバリデーション発火
            `project_name_${formIndex + 1}`,
            `project_category_${formIndex + 1}`,
            `industry_${formIndex + 1}`,
            `role_${formIndex + 1}`,
            `scale_${formIndex + 1}`,
            `overview_${formIndex + 1}`];
        const isValid = await Promise.all(fieldsToValidate.map(field => trigger(field)));

        const allValid = isValid.every(result => result);

        const hasEnvironmentError = checkEnvironmentErrors(formIndex);

        if (!allValid || hasEnvironmentError) {

            // エラー要素へスクロール
            setTimeout(() => {
                const firstErrorField = fieldsToValidate.find(field => errors[field]);
                if (firstErrorField) {
                    const errorElement = document.getElementById(firstErrorField);
                    if (errorElement) {
                        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        errorElement.focus();
                    }
                } else if (hasEnvironmentError) {
                    // システム/作業環境エラーがある場合、最初のエラーセクションへスクロール
                    const categories = ['server', 'client', 'dev', 'cloud', 'pc', 'network'];
                    for (const category of categories) {
                        if (errors.forms?.[formIndex]?.[`${category}_range`]) {
                            // 特定のフォームとカテゴリーのテーブルヘッダー要素を取得
                            const tableHeaderElement = document.querySelector(`[data-testid="form-${formIndex}"] th[data-category="${category}"]`);
                            if (tableHeaderElement) {
                                tableHeaderElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                break;
                            }
                        }
                    }
                }
            });

            return; // バリデーションエラーがある場合は処理を中断
        }

        // システム/作業環境の空の項目を削除
        const formData = { ...forms[formIndex] };

        // 各カテゴリの配列から空の項目を削除（全ての項目をチェック）
        const categories = ['server', 'client', 'dev', 'cloud', 'pc', 'network'];
        categories.forEach(category => {
            // 全ての項目に対してチェックを行う
            formData[category] = formData[category].filter((item) => {
                // typeとconの値が両方とも空でない場合は残す
                return item[`${category}_type`][0].value !== '' || item[`${category}_con`][0].value !== '';
            });

            // フィルタリング後に項目が0になった場合は、空の項目を1つ追加
            if (formData[category].length === 0) {
                formData[category] = [{
                    [`${category}_type`]: [{ value: '', name: `${category}_type_1` }],
                    [`${category}_con`]: [{ value: '', name: `${category}_con_1` }]
                }];
            }
        });

        // ここでフォームデータをサーバーに送信するなどの処理を行う
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/project`, {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(formData),
                credentials: "include",
            });
            const result = await response.json();
            if (response.status === 200 && result.status === 'OK') {
                clearStoredData();
                await fetchData();
                await fetchOptionsData();
                Swal.fire({ icon: 'success', title: successMessages.operation.created });
            } else {
                if(response.status === 401){
                    navigate(menu.ENGINEER.SIGNIN.PATH)
                } else {
                    throw new Error();
                }
            }
        } catch (e) {
            await Swal.fire({
                icon: 'error',
                html: `${e.front_message || errorMessages.api.server_error}`,
                customClass: {
                    htmlContainer: 'large-text'
                }
            })
        }
    };

    const addEnvironmentItem = (formIndex, category) => {
        setForms(forms.map((form, index) => {
            if (index === formIndex) {
            const uniqueId = `${category}_${formIndex}_${Date.now()}`;
            const newItem = {
                id: uniqueId,
                [`${category}_type`]: [{ value: '', name: `${category}_type_${form[category].length + 1}` }],
                [`${category}_con`]: [{ value: '', name: `${category}_con_${form[category].length + 1}` }]
            };
            return { ...form, [category]: [...form[category], newItem] };
            }
            return form;
        }));
    };


    const CATEGORY_SERVER = '1';
    const CATEGORY_CLIENT = '2';
    const CATEGORY_DEV = '3';
    const CATEGORY_CLOUD = '4';
    const CATEGORY_WORKING_PC = '5';
    const CATEGORY_NETWORK = '6';

    // react-selectのオプションを定義
    const [envType, setEnvType] = useState({
        serverTypeOptions: [],
        clientTypeOptions: [],
        devTypeOptions: [],
        cloudTypeOptions: [],
        pcTypeOptions: [],
        networkTypeOptions: [],
    });

    const deleteRow = (event, formIndex, category, itemIndex) => {
      // formsのstateを更新
      setForms(prevForms => {
            return prevForms.map((form, index) => {
              if (index === formIndex) {
                  const updatedForm = { ...form };
                  // カテゴリに応じて対応する配列から項目を削除
                  switch (category) {
                        case 'server':
                          updatedForm.server = form.server.filter((_, index) => index !== itemIndex);
                          break;
                      case 'client':
                          updatedForm.client = form.client.filter((_, index) => index !== itemIndex);
                          break;
                      case 'dev':
                          updatedForm.dev = form.dev.filter((_, index) => index !== itemIndex);
                          break;
                      case 'cloud':
                          updatedForm.cloud = form.cloud.filter((_, index) => index !== itemIndex);
                          break;
                      case 'pc':
                          updatedForm.pc = form.pc.filter((_, index) => index !== itemIndex);
                          break;
                      case 'network':
                          updatedForm.network = form.network.filter((_, index) => index !== itemIndex);
                          break;
                  }
                  return updatedForm;
              }
              return form;
          });
      });
    };
    
    const [itemNameToContentsMap, setItemNameToContentsMap] = useState(new Map());

    // サーバー、クライアント、開発、クラウドで、選択されたオプションに基づいて各conのセレクトボックスのoptionを変更
    const [serverConOptions, setServerConOptions] = useState([]);
    const [clientConOptions, setClientConOptions] = useState([]);
    const [devConOptions, setDevConOptions] = useState([]);
    const [cloudConOptions, setCloudConOptions] = useState([]);
    const [pcConOptions, setPcConOptions] = useState([]);
    const [networkConOptions, setNetworkConOptions] = useState([]);

    // 各環境のtypeのセレクトボックスのoptionを更新
    const handleEnvironmentTypeChange = (selectedOption, itemIndex, formIndex, category) => {
        if (selectedOption) {
            const updatedForms = [...forms];
            updatedForms[formIndex][category][itemIndex][`${category}_type`][0].value = selectedOption.label;
    
            // type値が変更された時のみ、対応するconのオプションを更新
            const categoryIdMap = {
                'server': CATEGORY_SERVER,
                'client': CATEGORY_CLIENT,
                'dev': CATEGORY_DEV,
                'cloud': CATEGORY_CLOUD,
                'pc': CATEGORY_WORKING_PC,
                'network': CATEGORY_NETWORK
            };
            
            const categoryId = categoryIdMap[category];
            if (selectedOption.value.startsWith(categoryId)) {
                const itemObject = itemNameToContentsMap.get(categoryId);
                const propertyValueArray = itemObject[selectedOption.label];
                const transformedOptions = propertyValueArray.map(item => ({
                    label: item.name,
                    value: item.id.toString()
                }));
                
                // 対応するカテゴリのオプション状態を更新
                switch(category) {
                    case 'server': setServerConOptions(transformedOptions); break;
                    case 'client': setClientConOptions(transformedOptions); break;
                    case 'dev': setDevConOptions(transformedOptions); break;
                    case 'cloud': setCloudConOptions(transformedOptions); break;
                    case 'pc': setPcConOptions(transformedOptions); break;
                    case 'network': setNetworkConOptions(transformedOptions); break;
                    default: break;
                }
            }
    
            // forms状態を更新
            setForms(updatedForms);
        } else {
            // 削除時の処理
            const updatedForms = [...forms];
            updatedForms[formIndex][category][itemIndex][`${category}_type`][0].value = '';
            setForms(updatedForms);
            
            // オプション状態をクリア
            switch(category) {
                case 'server': setServerConOptions([]); break;
                case 'client': setClientConOptions([]); break;
                case 'dev': setDevConOptions([]); break;
                case 'cloud': setCloudConOptions([]); break;
                case 'pc': setPcConOptions([]); break;
                case 'network': setNetworkConOptions([]); break;
                default: break;
            }
        }
    };

    // システム作業環境のバリデーション実行
    useEffect(() => {
        Object.keys(forms).forEach(formIndex => {
            const currentForm = forms[formIndex];
            
            // 各環境カテゴリーの定義
            const environmentCategories = [
                { 
                    category: 'server', 
                    items: currentForm.server, 
                    typeKey: 'server_type', 
                    conKey: 'server_con' 
                },
                { 
                    category: 'client', 
                    items: currentForm.client, 
                    typeKey: 'client_type', 
                    conKey: 'client_con' 
                },
                { 
                    category: 'dev', 
                    items: currentForm.dev, 
                    typeKey: 'dev_type', 
                    conKey: 'dev_con' 
                },
                { 
                    category: 'cloud', 
                    items: currentForm.cloud, 
                    typeKey: 'cloud_type', 
                    conKey: 'cloud_con' 
                },
                { 
                    category: 'pc', 
                    items: currentForm.pc, 
                    typeKey: 'pc_type', 
                    conKey: 'pc_con' 
                },
                { 
                    category: 'network', 
                    items: currentForm.network, 
                    typeKey: 'network_type', 
                    conKey: 'network_con' 
                }
            ];
            
            // 各カテゴリに対してバリデーションを実行
            environmentCategories.forEach(({ category, items, typeKey, conKey }) => {
                items.forEach((item, itemIndex) => {
                    const typeValue = item[typeKey][0].value;
                    const conValue = item[conKey][0].value;
                    
                    validateEnvironmentInputs(
                        parseInt(formIndex), 
                        itemIndex, 
                        conValue, 
                        typeValue ? { label: typeValue, value: typeValue } : null,
                        category
                    );
                });
            });
        });
    }, [forms]);
    
    // 共通バリデーション関数
    const validateEnvironmentInputs = (formIndex, itemIndex, selectedConOption, selectedOption, category) => {
        const typeValue = selectedOption ? selectedOption.label : '';
        const conValue  = selectedConOption ? selectedConOption : '';
        // エラー状態をクリア
        clearErrors(`forms[${formIndex}].${category}_range.${itemIndex}`);

        // 共通関数でチェック
        const errorMessage = environmentValidation(category, typeValue, conValue);

        // エラーがあればセット
        if (errorMessage !== true) {
            setError(`forms[${formIndex}].${category}_range.${itemIndex}`, {
                type: "manual",
                message: errorMessage,
            });
        }
    };
    // 各環境のconの値が変更された際のイベントハンドラ
    const handleEnvironmentConChange = (selectedOption, itemIndex, formIndex, category) => {
        const updatedForms = [...forms];
        
        // 値を更新
        updatedForms[formIndex][category][itemIndex][`${category}_con`][0].value = 
            selectedOption ? selectedOption.label : '';
        
        // 更新されたフォームをセット
        setForms(updatedForms);
    };

    // 表示・非表示の切り替え
    const [visibleStates, setVisibleStates] = useState(Array(forms.length).fill(true));

    const toggleVisibility = (index) => {
        const newVisibleStates = [...visibleStates];
        newVisibleStates[index] = !newVisibleStates[index]; // 非表示は表示、表示は非表示に切り替える
        setVisibleStates(newVisibleStates);
    };
    
    // プロジェクトの開始日を比較して、降順にソートする関数
    const sortProjectsByStartDate = (projectsToSort) => {
        if (projectsToSort && projectsToSort.length > 0) {
        return [...projectsToSort].sort((a, b) => {
            // 年の比較
            const yearA = parseInt(a.start_year || '0');
            const yearB = parseInt(b.start_year || '0');
            
            if (yearB !== yearA) {
            return yearB - yearA; // 年の降順
            }
            
            // 月の比較
            const monthA = parseInt(a.start_month || '0');
            const monthB = parseInt(b.start_month || '0');
            return monthB - monthA; // 月の降順
        });
        }
        return projectsToSort;
    };

    const fetchData = async () => {
      setIsLoading(true);
      try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/project`, {
            method: "GET",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            credentials: "include",
            });
            const result = await response.json();
            if(response.status === 200 && result.status === 'OK'){
                const data = result?.data;
                // データをソートして設定
                const sortedData = sortProjectsByStartDate(data.projectsBox.map((item) => item));
                reset({ projects: sortedData });
                setForms(sortedData);
                setOriginalData(sortedData);
                checkForEditData(sortedData);

                if (data?.skillsheetBox) {
                    // skillsheetBoxが存在する場合、skillsheetStateを更新
                    skillsheetState.forms = data.skillsheetBox.map((item) => item.skillsheet);
                }
            } else {
                if(response.status === 401){
                    navigate(menu.ENGINEER.SIGNIN.PATH)
                } else if(response.status === 404) {
                    const error = new Error();
                    error.front_message = errorMessages.common.data_not_found;
                    throw error;
                } else {
                    const error = new Error();
                    error.front_message = errorMessages.common.data_fetch_failed;
                    throw error;
                }
            }
        } catch(e) {
            await Swal.fire({
                icon: 'error',
                html: `${e.front_message || errorMessages.api.server_error}`,
                customClass: {
                    htmlContainer: 'large-text'
                }
            })
        } finally {
            setIsLoading(false);
        }
    };
    const [optionsData, setOptionsData] = useState(null);

    // プロジェクト区分、業種、役割、規模、システム/環境設定の各オプションを取得
    const fetchOptionsData = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/project/options`, {
                method: "GET",
                headers: {
                    'Accept': "application/json",
                    "Content-Type": "application/json",
                },
                credentials: 'include'
            });
            const result = await response.json();  
            if(response.status === 200 && result.status === 'OK'){
                const data = result?.data
                // プロジェクト区分、業種、役割、規模のオプションを設定
                setOptionsData(data);
                
                if (data.formattedWorkDevEnvironment) {
                    // 環境タイプの選択肢を更新
                    setEnvType(data.formattedWorkDevEnvironment.itemName);
                    
                    // カテゴリマッピングをMapに変換して設定
                    const newMap = new Map(
                        Object.entries(data.formattedWorkDevEnvironment.content)
                    );
                    
                    setItemNameToContentsMap(newMap);
                }
            } else {
                if(response.status === 401){
                    navigate(menu.ENGINEER.SIGNIN.PATH)
                } else {
                    throw new Error();
                }
            }
        } catch(e) {
            await Swal.fire({
                icon: 'error',
                html: `${e.front_message || errorMessages.api.server_error}`,
                customClass: {
                    htmlContainer: 'large-text'
                }
            })
        }
    };

    useEffect(() => {
        fetchOptionsData();
        fetchData();
    }, [reset]);

    const [charCounts, setCharCounts] = useState({});

    useEffect(() => {
        const newCounts = {};
        forms.forEach((form, index) => {
            newCounts[index] = form.overview?.length || 0;
        });
        setCharCounts(newCounts);
    }, [forms]);

    const handleTextAreaChange = (e, formIndex) => {
        const value = e.target.value;
        setCharCounts(prev => ({
            ...prev,
            [formIndex]: value.length
        }));
        handleInputChange(e, formIndex, 'overview');
    };

    return (
        <>
          {isLoading && <div style={{ textAlign: 'center', padding: '40px 0', fontSize: '1.2em' }}>Loading...</div>}
 
          {!isLoading && forms.length === 0 && (
                <div className={sty['form']} key="new" onClick={() => addForm()} style={{cursor: 'pointer'}}>
                    <div className={`${sty['form-tab__content-inner']} ${sty['form-tab__content-inner-pt-minus']}`} key="0">{/* フォームが表示されていないときだけ toggleVisibility を実行 */}
                        <div>
                            <div style={{textAlign: 'center', color:'darkgray'}}>＋新規作成</div>
                        </div>
                    </div>    
                </div>
          )}
          {!isLoading && forms.map((form, formIndex) => (
              <div className={sty['form']} key={formIndex} ref={(el) => setFormRef(formIndex, el)} data-testid={`form-${formIndex}`}>
                  <div className={`${sty['form-tab__content-inner']} ${sty['form-tab__content-inner-pt-minus']}`} key={formIndex}
                      onClick={() => !visibleStates[formIndex] && toggleVisibility(formIndex)}
                  >{/* フォームが表示されていないときだけ toggleVisibility を実行 */}
                      <div className={sty['form__tit-flex']}>
                          <div className={sty['form__tit-inner']}>
                              <div className={sty['form__no']}>{formIndex + 1}</div>
                              <div className={sty['form__tit-flex-inner']}>
                                  <h2 className={sty['form__tit form__tit-pj']}>{form.project_name || `プロジェクト${formIndex + 1}`}</h2>
                                  <div className={sty['form__flex-paragraphs']}>
                                      <p className={sty['form__pj-term']}>
                                          {form.start_year}年{form.start_month}月〜{form.is_participating === 'false' ? (
                                              <span>{form.end_year}年{form.end_month}月</span>
                                          ) : (
                                              <span>現在</span> // 参画中radioを選んだ場合
                                          )}
                                      </p>
                                      {form?.updated_at ? <p className={sty['form__updated-date']}><img src="/images/update_24.svg" width="16" height="16"/> {dayjs(form?.updated_at)?.format("YYYY/MM/DD HH:mm")}</p>: null}
                                  </div>
                              </div>
                          </div>

                          <button type="button" onClick={() => toggleVisibility(formIndex)} className={`${sty['show-more-less']} ${sty['show-more-less-pj']}`}>
                              {visibleStates[formIndex] ? '▲' : '▼'}
                          </button>
                      </div>

                      {visibleStates[formIndex] && <div className={sty['form__tit-liner']}></div>}

                      {visibleStates[formIndex] && (
                          <div className={sty['form__inputs-outer']}>
                              <div className={`${sty['form__flex']} ${sty['form__baseline-cent']}`}>
                                  <div className={`${sty['form__label-flex']} ${sty['form__label-flex-mb']}`}>
                                      <div className={sty['form__label']}>参画ステータス</div>
                                      <div className={sty['required']}>必須</div>
                                  </div>
                                  <div className={sty['form__input']}>
                                      <div className={sty['radios']}>
                                          <div className={sty['form__input-outer-radio']}>
                                              <input
                                                  type="radio"
                                                  id={`is_participating_${formIndex + 1}_true`}  // idを追加
                                                  name={`is_participating_${formIndex + 1}`}
                                                  value="true"
                                                  checked={form.is_participating === 'true'}
                                                  onChange={(e) => handleInputChange(e, formIndex, 'is_participating')} />
                                              <label htmlFor={`is_participating_${formIndex + 1}_true`}>参画中</label>  {/* for属性を修正 */}
                                          </div>
                                          <div className={sty['form__input-outer-radio']}>
                                              <input
                                                  type="radio"
                                                  id={`is_participating_${formIndex + 1}_false`}  // idを追加
                                                  name={`is_participating_${formIndex + 1}`}
                                                  value="false"
                                                  checked={form.is_participating === 'false'}
                                                  onChange={(e) => handleInputChange(e, formIndex, 'is_participating')} />
                                              <label htmlFor={`is_participating_${formIndex + 1}_false`}>終了</label>  {/* for属性を修正 */}
                                          </div>
                                      </div>

                                  </div>

                              </div>
                              <div className={sty['form__flex']}>
                                  <div className={`${sty['form__label-flex']} ${sty['form__label-flex-mb']}`}>
                                      <div className={sty['form__label']}>期間</div>
                                      <div className={sty['required']}>必須</div>
                                  </div>

                                  <div className={sty['form__input']}>
                                      { isSPDevice ? (
                                      <>
                                          {/* SP版 */}
                                          <div className={`${sty['form__input-flex']} ${sty['form__input-flex-display-block']}`}>
                                              <div className={sty['form__pj-term-sp']}>
                                                  <div className={sty['form__select-outer']}>
                                                      <select
                                                          {...register(`start_year_${formIndex + 1}`, validationSchema.select)}
                                                          id={`start_year_${formIndex + 1}`}
                                                          value={form.start_year}
                                                          onChange={(e) => handleInputChange(e, formIndex, 'start_year')}
                                                          aria-label="SP期間開始年"
                                                          >
                                                          <option hidden value="">選択</option>
                                                          {years.sort((a, b) => b - a).map((y) => (
                                                              <option key={y} value={y}>
                                                                  {y + '年'}
                                                              </option>
                                                          ))}
                                                      </select>
                                                      {errors[`start_year_${formIndex + 1}`] && <p className="form__validation-msg">{errors[`start_year_${formIndex + 1}`].message}</p>}
                                                  </div>
                                                  <div className={sty['form__select-outer']}>
                                                      <select
                                                          {...register(`start_month_${formIndex + 1}`, validationSchema.select)}
                                                          id={`start_month_${formIndex + 1}`}
                                                          value={form.start_month}
                                                          onChange={(e) => handleInputChange(e, formIndex, 'start_month')}
                                                          aria-label="SP期間開始月"
                                                          >
                                                          <option hidden value="">選択</option>
                                                              {months.map((m) => (
                                                                  <option key={m} value={m}>
                                                                      {m + '月'}
                                                                  </option>
                                                              ))}
                                                      </select>
                                                      {errors[`start_month_${formIndex + 1}`] && <p className="form__validation-msg">{errors[`start_month_${formIndex + 1}`].message}</p>}
                                                  </div>
                                                      <span>から</span>
                                              </div>

                                              {form.is_participating === 'false' && (
                                                  <div className={sty['form__pj-term-sp']}>
                                                      <div className={sty['form__select-outer']}>
                                                          <select
                                                              {...register(`end_year_${formIndex + 1}`, validationSchema.select)}
                                                              id={`end_year_${formIndex + 1}`}
                                                              value={form.end_year}
                                                              onChange={(e) => handleInputChange(e, formIndex, 'end_year')}
                                                              aria-label="SP期間終了年"
                                                              >
                                                              <option hidden value="">選択</option>
                                                              {years.sort((a, b) => b - a).map((y) => (
                                                                  <option key={y} value={y}>
                                                                      {y + '年'}
                                                                  </option>
                                                              ))}
                                                          </select>
                                                          {errors[`end_year_${formIndex + 1}`] && <p className="form__validation-msg">{errors[`end_year_${formIndex + 1}`].message}</p>}
                                                      </div>
                                                      <div className={sty['form__select-outer']}>
                                                          <select
                                                              {...register(`end_month_${formIndex + 1}`, validationSchema.select)}
                                                              id={`end_month_${formIndex + 1}`}
                                                              value={form.end_month}
                                                              onChange={(e) => handleInputChange(e, formIndex, 'end_month')}
                                                              aria-label="SP期間終了月"
                                                              >
                                                              <option hidden value="">選択</option>
                                                                      {months.map((m) => (
                                                                          <option key={m} value={m}>
                                                                              {m + '月'}
                                                                  </option>
                                                              ))}
                                                          </select>
                                                          {errors[`end_month_${formIndex + 1}`] && <p className="form__validation-msg">{errors[`end_month_${formIndex + 1}`].message}</p>}
                                                      </div>
                                                      <span>まで</span>
                                                  </div>
                                              )}
                                          </div>
                                      </>) : (
                                      <>
                                          {/* PC版 */}
                                          <div className={`${sty['form__input-flex']} ${sty['form__pj-term-pc']}`}>
                                              <div className={sty['form__select-outer']}>
                                                  <select
                                                      {...register(`start_year_${formIndex + 1}`, validationSchema.select)}
                                                      id={`start_year_${formIndex + 1}`}
                                                      value={form.start_year}
                                                      onChange={(e) => handleInputChange(e, formIndex, 'start_year')}
                                                      aria-label="PC期間開始年"
                                                      >
                                                      <option hidden value="">選択</option>
                                                      {years.sort((a, b) => b - a).map((y) => (
                                                          <option key={y} value={y}>
                                                              {y + '年'}
                                                          </option>
                                                      ))}
                                                  </select>
                                                  {errors[`start_year_${formIndex + 1}`] && <p className="form__validation-msg">{errors[`start_year_${formIndex + 1}`].message}</p>}
                                              </div>

                                              <div className={sty['form__select-outer']}>
                                                  <select
                                                      {...register(`start_month_${formIndex + 1}`, validationSchema.select)}
                                                      id={`start_month_${formIndex + 1}`}
                                                      value={form.start_month}
                                                      onChange={(e) => handleInputChange(e, formIndex, 'start_month')}
                                                      aria-label="PC期間開始月"
                                                      >
                                                      <option hidden value="">選択</option>
                                                              {months.map((m) => (
                                                                  <option key={m} value={m}>
                                                                      {m + '月'}
                                                          </option>
                                                      ))}
                                                  </select>
                                                  {errors[`start_month_${formIndex + 1}`] && <p className="form__validation-msg">{errors[`start_month_${formIndex + 1}`].message}</p>}
                                              </div>

                                              <span>から</span>
                                              {form.is_participating === 'false' && (
                                                  <>
                                                      <div className={sty['form__select-outer']}>
                                                          <select
                                                              {...register(`end_year_${formIndex + 1}`, validationSchema.select)}
                                                              id={`end_year_${formIndex + 1}`}
                                                              value={form.end_year}
                                                              onChange={(e) => handleInputChange(e, formIndex, 'end_year')}
                                                              aria-label="PC期間終了年"
                                                              >
                                                              <option hidden value="">選択</option>
                                                              {years.sort((a, b) => b - a).map((y) => (
                                                                  <option key={y} value={y}>
                                                                      {y + '年'}
                                                                  </option>
                                                              ))}
                                                          </select>
                                                          {errors[`end_year_${formIndex + 1}`] && <p className="form__validation-msg">{errors[`end_year_${formIndex + 1}`].message}</p>}
                                                      </div>

                                                      <div className={sty['form__select-outer']}>
                                                          <select
                                                              {...register(`end_month_${formIndex + 1}`, validationSchema.select)}
                                                              id={`end_month_${formIndex + 1}`}
                                                              value={form.end_month}
                                                              onChange={(e) => handleInputChange(e, formIndex, 'end_month')}
                                                              aria-label="PC期間終了月"
                                                              >
                                                              <option hidden value="">選択</option>
                                                              {months.map((m) => (
                                                                  <option key={m} value={m}>
                                                                      {m + '月'}
                                                                  </option>
                                                              ))}
                                                          </select>
                                                          {errors[`end_month_${formIndex + 1}`] && <p className="form__validation-msg">{errors[`end_month_${formIndex + 1}`].message}</p>}
                                                      </div>
                                                      <span>まで</span>
                                                  </>
                                              )}
                                          </div>
                                          <p className="form__validation-msg">
                                              {errors.forms?.[formIndex]?.date_range?.message}
                                          </p>
                                      </>) 
                                      }
                                  </div>
                              </div>

                              <div className={sty['form__flex']}>
                                  <div className={`${sty['form__label-flex']} ${sty['form__label-flex-mb']}`}>
                                      <div className={sty['form__label']}>プロジェクト名</div>
                                      <div className={sty['required']}>必須</div>
                                  </div>
                                  <div className={sty['form__input']}>
                                      <input {...register(`project_name_${formIndex + 1}`, validationSchema.project_name)}
                                          id={`project_name_${formIndex + 1}`}
                                          type="text"
                                          value={form.project_name}
                                          placeholder="国内大手製薬会社常駐 Oracleデータベース管理・運用業務"
                                          onChange={(e) => handleInputChange(e, formIndex, 'project_name')} />
                                      {errors[`project_name_${formIndex + 1}`] && <p className="form__validation-msg">{errors[`project_name_${formIndex + 1}`].message}</p>}
                                  </div>
                              </div>

                              <div className={sty['form__flex']}>
                                  <div className={`${sty['form__label-flex']} ${sty['form__label-flex-mb']}`}>
                                      <div className={sty['form__label']}>プロジェクト区分</div>
                                      <div className={sty['required']}>必須</div>
                                  </div>
                                  <div className={sty['form__input']}>
                                      {/* プロジェクト区分のプルダウン */}
                                      <select
                                          {...register(`project_category_${formIndex + 1}`, validationSchema.select)}
                                          id={`project_category_${formIndex + 1}`}
                                          value={form.project_category}
                                          onChange={(e) => handleInputChange(e, formIndex, 'project_category')}
                                          aria-label="プロジェクト区分"
                                      >
                                          <option hidden value="">選択</option>
                                          {optionsData?.projectCategories.map(category => (
                                              <option key={category.id} value={category.id}>
                                                  {category.name}
                                              </option>
                                          ))}
                                      </select>
                                      {errors[`project_category_${formIndex + 1}`] && 
                                          <p className="form__validation-msg">{errors[`project_category_${formIndex + 1}`].message}</p>
                                      }
                                  </div>
                              </div>

                              <div className={sty['form__flex']}>
                                  <div className={`${sty['form__label-flex']} ${sty['form__label-flex-mb']}`}>
                                      <div className={sty['form__label']}>業種</div>
                                      <div className={sty['required']}>必須</div>
                                  </div>
                                  <div className={sty['form__input']}>
                                      {/* 業種のプルダウン */}
                                      <select
                                          {...register(`industry_${formIndex + 1}`, validationSchema.select)}
                                          id={`industry_${formIndex + 1}`}
                                          value={form.industry}
                                          onChange={(e) => handleInputChange(e, formIndex, 'industry')}
                                          aria-label="業種"
                                      >
                                          <option hidden value="">選択</option>
                                          {optionsData?.industries.map(industry => (
                                              <option key={industry.id} value={industry.id}>
                                                  {industry.name}
                                              </option>
                                          ))}
                                      </select>
                                      {errors[`industry_${formIndex + 1}`] && 
                                          <p className="form__validation-msg">{errors[`industry_${formIndex + 1}`].message}</p>
                                      }
                                  </div>
                              </div>

                              <div className={sty['form__flex']}>
                                  <div className={`${sty['form__label-flex']} ${sty['form__label-flex-mb']}`}>
                                      <div className={sty['form__label']}>役割</div>
                                      <div className={sty['required']}>必須</div>
                                  </div>
                                  <div className={sty['form__input']}>
                                      {/* 役割のプルダウン */}
                                      <select
                                          {...register(`role_${formIndex + 1}`, validationSchema.select)}
                                          id={`role_${formIndex + 1}`}
                                          value={form.role}
                                          onChange={(e) => handleInputChange(e, formIndex, 'role')}
                                          aria-label="役割"
                                      >
                                          <option hidden value="">選択</option>
                                          {optionsData?.roles.map(role => (
                                              <option key={role.id} value={role.id}>
                                                  {role.name}
                                              </option>
                                          ))}
                                      </select>
                                      {errors[`role_${formIndex + 1}`] && 
                                          <p className="form__validation-msg">{errors[`role_${formIndex + 1}`].message}</p>
                                      }
                                  </div>
                              </div>

                              <div className={sty['form__flex']}>
                                  <div className={`${sty['form__label-flex']} ${sty['form__label-flex-mb']}`}>
                                      <div className={sty['form__label']}>規模</div>
                                      <div className={sty['required']}>必須</div>
                                  </div>
                                  <div className={sty['form__input']}>
                                      {/* 規模のプルダウン */}
                                      <select
                                          {...register(`scale_${formIndex + 1}`, validationSchema.select)}
                                          id={`scale_${formIndex + 1}`}
                                          value={form.scale}
                                          onChange={(e) => handleInputChange(e, formIndex, 'scale')}
                                          aria-label="規模"
                                      >
                                          <option hidden value="">選択</option>
                                          {optionsData?.scales.map(scale => (
                                              <option key={scale.id} value={scale.id}>
                                                  {scale.name}
                                              </option>
                                          ))}
                                      </select>
                                      {errors[`scale_${formIndex + 1}`] && 
                                          <p className="form__validation-msg">{errors[`scale_${formIndex + 1}`].message}</p>
                                      }
                                  </div>
                              </div>

                              <div className={sty['form__flex']}>
                                  <div className={`${sty['form__label-flex']} ${sty['form__label-flex-mb']}`}>
                                      <div className={sty['form__label']}>業務概要</div>
                                      <div className={sty['required']}>必須</div>
                                  </div>
                                  <div className={sty['form__input']}>
                                        <div className={sty['textarea-wrapper']}>
                                            <textarea
                                                {...register(`overview_${formIndex + 1}`, validationSchema.overview)}
                                                id={`overview_${formIndex + 1}`}
                                                value={form.overview}
                                                onChange={(e) => handleTextAreaChange(e, formIndex)}
                                                rows="11"
                                                data-testid="overview"
                                                placeholder={`◼︎業務概要
・製薬会社常駐にて顧客の研究開発業務へのIT支援
  ー サーバー・データベース管理・運用
  ー 業務システムの開発

◼︎担当業務
・データベースバックアップ（Recovery Manager, Oracle EnterpriseManager）
・研究データ最適化（インデックス再構築、SQLチューニング）
・化合物管理システム開発（Web）
  ー 詳細設計～テスト（設計書、テスト仕様書作成含む）`}>
                                            </textarea>
                                            <div className={`${sty['char-count']} ${charCounts[formIndex] > 400 ? sty['warning'] : ''} ${charCounts[formIndex] > 500 ? sty['exceeded'] : ''}`}>
                                                {`${charCounts[formIndex] || 0} / 500`}
                                            </div>
                                        </div>
                                      {errors[`overview_${formIndex + 1}`] && <p className="form__validation-msg">{errors[`overview_${formIndex + 1}`].message}</p>}
                                  </div>
                              </div>

                              <div className={sty['form__flex']}>
                                  <div className={sty['form__label-flex']}>
                                      <div className={`${sty['form__label']} ${sty['form__label-flex-mb']}`}>担当工程</div>
                                  </div>
                                  <div className={sty['form__input']}>
                                      <div>
                                          <div className={sty['form__input-flex']}>
                                              <label htmlFor={`requirement_analysis_${formIndex + 1}`} className={sty['form__check-label']}>
                                                  <input type="checkbox" id={`requirement_analysis_${formIndex + 1}`} 
                                                      {...register(`requirement_analysis_${formIndex + 1}`)}
                                                      checked={form.process.requirement_analysis}
                                                      onChange={(e) => {
                                                          const isChecked = e.target.checked;
                                                          handleInputChange(e, formIndex, 'requirement_analysis', isChecked);
                                                      }}
                                                      />要件定義
                                              </label>
                                              <label htmlFor={`basic_design_${formIndex + 1}`} className={sty['form__check-label']}>
                                                  <input type="checkbox" id={`basic_design_${formIndex + 1}`} 
                                                      {...register(`basic_design_${formIndex + 1}`)}
                                                      checked={form.process.basic_design}
                                                      onChange={(e) => {
                                                          const isChecked = e.target.checked;
                                                          handleInputChange(e, formIndex, 'basic_design', isChecked);
                                                      }}
                                                  />基本設計
                                              </label>
                                          </div>
                                          <div className={sty['form__input-flex']}>
                                              <label htmlFor={`detailed_design_${formIndex + 1}`} className={sty['form__check-label']}>
                                                  <input type="checkbox" id={`detailed_design_${formIndex + 1}`} 
                                                      {...register(`detailed_design_${formIndex + 1}`)}
                                                      checked={form.process.detailed_design}
                                                      onChange={(e) => {
                                                          const isChecked = e.target.checked;
                                                          handleInputChange(e, formIndex, 'detailed_design', isChecked);
                                                      }}
                                                  />詳細設計
                                              </label>
                                              <label htmlFor={`coding_${formIndex + 1}`} className={sty['form__check-label']}>
                                                  <input type="checkbox" id={`coding_${formIndex + 1}`} 
                                                      {...register(`coding_${formIndex + 1}`)}
                                                      checked={form.process.coding}
                                                      onChange={(e) => {
                                                          const isChecked = e.target.checked;
                                                          handleInputChange(e, formIndex, 'coding', isChecked);
                                                      }}
                                                  />実装・構築
                                              </label>
                                          </div>
                                          <div className={sty['form__input-flex']}>
                                              <label htmlFor={`unit_test_${formIndex + 1}`} className={sty['form__check-label']}>
                                                  <input type="checkbox" id={`unit_test_${formIndex + 1}`} 
                                                      {...register(`unit_test_${formIndex + 1}`)}
                                                      checked={form.process.unit_test}
                                                      onChange={(e) => {
                                                          const isChecked = e.target.checked;
                                                          handleInputChange(e, formIndex, 'unit_test', isChecked);
                                                      }}
                                                  />単体テスト
                                              </label>
                                              <label htmlFor={`integration_test_${formIndex + 1}`} className={sty['form__check-label']}>
                                                  <input type="checkbox" id={`integration_test_${formIndex + 1}`} 
                                                      {...register(`integration_test_${formIndex + 1}`)}
                                                      checked={form.process.integration_test}
                                                      onChange={(e) => {
                                                          const isChecked = e.target.checked;
                                                          handleInputChange(e, formIndex, 'integration_test', isChecked);
                                                      }}
                                                  />結合テスト
                                              </label>
                                          </div>
                                          <div className={sty['form__input-flex']}>
                                              <label htmlFor={`system_test_${formIndex + 1}`} className={sty['form__check-label']}>
                                                  <input type="checkbox" id={`system_test_${formIndex + 1}`} 
                                                      {...register(`system_test_${formIndex + 1}`)}
                                                      checked={form.process.system_test}
                                                      onChange={(e) => {
                                                          const isChecked = e.target.checked;
                                                          handleInputChange(e, formIndex, 'system_test', isChecked);
                                                      }}
                                                  />総合テスト
                                              </label>
                                              <label htmlFor={`load_test_${formIndex + 1}`} className={sty['form__check-label']}>
                                                  <input type="checkbox" id={`load_test_${formIndex + 1}`} 
                                                      {...register(`load_test_${formIndex + 1}`)}
                                                      checked={form.process.load_test}
                                                      onChange={(e) => {
                                                          const isChecked = e.target.checked;
                                                          handleInputChange(e, formIndex, 'load_test', isChecked);
                                                      }}
                                                  />負荷テスト
                                              </label>
                                          </div>
                                          <div className={sty['form__input-flex']}>
                                              <label htmlFor={`security_test_${formIndex + 1}`} className={sty['form__check-label']}>
                                                  <input type="checkbox" id={`security_test_${formIndex + 1}`} 
                                                      {...register(`security_test_${formIndex + 1}`)}
                                                      checked={form.process.security_test}
                                                      onChange={(e) => {
                                                          const isChecked = e.target.checked;
                                                          handleInputChange(e, formIndex, 'security_test', isChecked);
                                                      }}
                                                  />セキュリティテスト
                                              </label>
                                              <label htmlFor={`operation_${formIndex + 1}`} className={sty['form__check-label']}>
                                                  <input type="checkbox" id={`operation_${formIndex + 1}`} 
                                                      {...register(`operation_${formIndex + 1}`)}
                                                      checked={form.process.operation}
                                                      onChange={(e) => {
                                                          const isChecked = e.target.checked;
                                                          handleInputChange(e, formIndex, 'operation', isChecked);
                                                      }}
                                                  />保守・運用
                                              </label>
                                          </div>
                                      </div>
                                  </div>
                              </div>

                              <div className={sty['form__flex']}>
                                  <div className={sty['form__label-flex']}>
                                      <div className={`${sty['form__label']} ${sty['form__label-flex-mb']}`}>システム/作業環境</div>
                                  </div>
                                  <div className={sty['form__input']}>
                                      <div>
                                        {/* PC版テーブル */}
                                        <table className={sty['env-table-pc']}>
                                            <tbody>
                                            <tr>
                                                <th colSpan="3" data-category="server">サーバー</th>
                                            </tr>

                                            {form.server.map((serverItem, serverIndex) => (
                                                <React.Fragment key={serverIndex}>
                                                <tr>
                                                    {/* サーバータイプ */}
                                                    <td className={sty['form__td-work-env-type']}>
                                                    {serverItem.server_type.map((type, typeIndex) => (
                                                        <CreatableSelect
                                                        {...register(type.name, validationSchema.select)}
                                                        key={typeIndex}
                                                        value={{ label: type.value, value: "" }}
                                                        isClearable
                                                        placeholder="選択"
                                                        options={envType.serverTypeOptions}
                                                        onChange={(selectedOption) =>
                                                            handleEnvironmentTypeChange(selectedOption, serverIndex, formIndex, 'server')
                                                        }
                                                        className={sty['form__select-env-type']}
                                                        styles={customStyles}
                                                        theme={(theme) => ({
                                                            ...theme,
                                                            borderRadius: 0,
                                                        })}
                                                        noOptionsMessage={() => 'テキストを入力して新規作成'}
                                                        formatCreateLabel={(inputValue) => `クリックで"${inputValue}"を新規作成`}
                                                        aria-label="サーバータイプ"
                                                        />
                                                    ))}
                                                    </td>

                                                    {/* サーバー詳細 */}
                                                    <td className={sty['form__td-work-env-con']}>
                                                    {serverItem.server_con.map((con, conIndex) => (
                                                        <CreatableSelect
                                                        key={conIndex}
                                                        {...register(con.name, validationSchema.server)}
                                                        value={{ label: con.value, value: "" }}
                                                        type="text"
                                                        isClearable
                                                        placeholder="選択"
                                                        options={serverConOptions}
                                                        onChange={(selectedOption) =>
                                                            handleEnvironmentConChange(selectedOption, serverIndex, formIndex, 'server')
                                                        }
                                                        className={sty['form__select-env-con']}
                                                        styles={customStyles}
                                                        theme={(theme) => ({ ...theme, borderRadius: 0 })}
                                                        noOptionsMessage={() => 'テキストを入力して新規作成'}
                                                        formatCreateLabel={(inputValue) => `クリックで"${inputValue}"を新規作成`}
                                                        aria-label="サーバー詳細"
                                                        />
                                                    ))}
                                                    </td>

                                                    {/* 削除ボタン */}
                                                    {form.server.length > 1 && (
                                                    <td className={sty['row-delete']}>
                                                        <button
                                                        type="button"
                                                        className={sty['delete-each-btn']}
                                                        onClick={(event) => deleteRow(event, formIndex, 'server', serverIndex)}
                                                        >
                                                        削除
                                                        </button>
                                                    </td>
                                                    )}
                                                </tr>

                                                {/* バリデーションエラー */}
                                                {errors.forms?.[formIndex]?.server_range?.[serverIndex]?.message && (
                                                    <tr>
                                                    <td colSpan="3" className="form__validation-msg">
                                                        {errors.forms[formIndex].server_range[serverIndex].message}
                                                    </td>
                                                    </tr>
                                                )}
                                                </React.Fragment>
                                            ))}
                                            </tbody>
                                        </table>

                                        {/* SP版縦並びレイアウト */}
                                        <div className={sty['env-layout-sp']}>
                                            <div className={sty['env-section-title']}>サーバー</div>
                                            {form.server.map((serverItem, serverIndex) => (
                                                <div key={serverIndex} className={sty['env-item-sp']}>
                                                    <div className={sty['env-item-content']}>
                                                        <div className={sty['env-input-group']}>
                                                            <label className={sty['env-label']}>サーバータイプ</label>
                                                            {serverItem.server_type.map((type, typeIndex) => (
                                                                <CreatableSelect
                                                                {...register(type.name, validationSchema.select)}
                                                                key={typeIndex}
                                                                value={{ label: type.value, value: "" }}
                                                                isClearable
                                                                placeholder="選択"
                                                                options={envType.serverTypeOptions}
                                                                onChange={(selectedOption) =>
                                                                    handleEnvironmentTypeChange(selectedOption, serverIndex, formIndex, 'server')
                                                                }
                                                                className={sty['form__select-env-type-sp']}
                                                                styles={customStyles}
                                                                theme={(theme) => ({
                                                                    ...theme,
                                                                    borderRadius: 0,
                                                                })}
                                                                noOptionsMessage={() => 'テキストを入力して新規作成'}
                                                                formatCreateLabel={(inputValue) => `クリックで新規作成`}
                                                                aria-label="サーバータイプ"
                                                                />
                                                            ))}
                                                        </div>
                                                        
                                                        <div className={sty['env-input-group']}>
                                                            <label className={sty['env-label']}>サーバー詳細</label>
                                                            {serverItem.server_con.map((con, conIndex) => (
                                                                <CreatableSelect
                                                                key={conIndex}
                                                                {...register(con.name, validationSchema.server)}
                                                                value={{ label: con.value, value: "" }}
                                                                type="text"
                                                                isClearable
                                                                placeholder="選択"
                                                                options={serverConOptions}
                                                                onChange={(selectedOption) =>
                                                                    handleEnvironmentConChange(selectedOption, serverIndex, formIndex, 'server')
                                                                }
                                                                className={sty['form__select-env-con-sp']}
                                                                styles={customStyles}
                                                                theme={(theme) => ({ ...theme, borderRadius: 0 })}
                                                                noOptionsMessage={() => 'テキストを入力して新規作成'}
                                                                formatCreateLabel={(inputValue) => `クリックで新規作成`}
                                                                aria-label="サーバー詳細"
                                                                />
                                                            ))}
                                                        </div>
                                                        {/* バリデーションエラー */}
                                                        {errors.forms?.[formIndex]?.server_range?.[serverIndex]?.message && (
                                                            <div className="form__validation-msg">
                                                                {errors.forms[formIndex].server_range[serverIndex].message}
                                                            </div>
                                                        )}
                                                        {/* 削除ボタン */}
                                                        {form.server.length > 1 && (
                                                            <div className={sty['env-delete-btn-bottom']}>
                                                                <button
                                                                type="button"
                                                                className={sty['delete-each-btn']}
                                                                onClick={(event) => deleteRow(event, formIndex, 'server', serverIndex)}
                                                                >
                                                                削除
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        </div>
                                        <button type="button" 
                                                onClick={() => addEnvironmentItem(formIndex, 'server')}
                                                className={sty['plus-each-form']}
                                                data-testid="serverPls">+
                                        </button>
                                  </div>
                              </div>

                              {/* クライアント */}
                              <div className={sty['form__flex']}>
                                  <div className={sty['form__label-flex']}></div>
                                  <div className={sty['form__input']}>
                                      <div>
                                        {/* PC版テーブル */}
                                        <table className={sty['env-table-pc']}>
                                            <tbody>
                                            <tr>
                                                <th colSpan="3" data-category="client">クライアント</th>
                                            </tr>

                                            {form.client.map((clientItem, clientIndex) => (
                                                <React.Fragment key={clientIndex}>
                                                <tr>
                                                    {/* クライアントタイプ */}
                                                    <td className={sty['form__td-work-env-type']}>
                                                    {clientItem.client_type.map((type, typeIndex) => (
                                                        <CreatableSelect
                                                        {...register(type.name, validationSchema.select)}
                                                        key={typeIndex}
                                                        value={{ label: type.value, value: "" }}
                                                        isClearable
                                                        placeholder="選択"
                                                        options={envType.clientTypeOptions}
                                                        onChange={(selectedOption) =>
                                                            handleEnvironmentTypeChange(selectedOption, clientIndex, formIndex, 'client')
                                                        }
                                                        className={sty['form__select-env-type']}
                                                        styles={customStyles}
                                                        theme={(theme) => ({
                                                            ...theme,
                                                            borderRadius: 0,
                                                        })}
                                                        noOptionsMessage={() => 'テキストを入力して新規作成'}
                                                        formatCreateLabel={(inputValue) => `クリックで"${inputValue}"を新規作成`}
                                                        aria-label="クライアントタイプ"
                                                        />
                                                    ))}
                                                    </td>

                                                    {/* クライアント詳細 */}
                                                    <td className={sty['form__td-work-env-con']}>
                                                    {clientItem.client_con.map((con, conIndex) => (
                                                        <CreatableSelect
                                                        key={conIndex}
                                                        {...register(con.name, validationSchema.client)}
                                                        value={{ label: con.value, value: "" }}
                                                        type="text"
                                                        isClearable
                                                        placeholder="選択"
                                                        options={clientConOptions}
                                                        onChange={(selectedOption) =>
                                                            handleEnvironmentConChange(selectedOption, clientIndex, formIndex, 'client')
                                                        }
                                                        className={sty['form__select-env-con']}
                                                        styles={customStyles}
                                                        theme={(theme) => ({ ...theme, borderRadius: 0 })}
                                                        noOptionsMessage={() => 'テキストを入力して新規作成'}
                                                        formatCreateLabel={(inputValue) => `クリックで"${inputValue}"を新規作成`}
                                                        aria-label="クライアント詳細"
                                                        />
                                                    ))}
                                                    </td>

                                                    {/* 削除ボタン */}
                                                    {form.client.length > 1 && (
                                                    <td className={sty['row-delete']}>
                                                        <button
                                                        type="button"
                                                        className={sty['delete-each-btn']}
                                                        onClick={(event) => deleteRow(event, formIndex, 'client', clientIndex)}
                                                        >
                                                        削除
                                                        </button>
                                                    </td>
                                                    )}
                                                </tr>

                                                {/* バリデーションエラー */}
                                                {errors.forms?.[formIndex]?.client_range?.[clientIndex]?.message && (
                                                    <tr>
                                                    <td colSpan="3" className="form__validation-msg">
                                                        {errors.forms[formIndex].client_range[clientIndex].message}
                                                    </td>
                                                    </tr>
                                                )}
                                                </React.Fragment>
                                            ))}
                                            </tbody>
                                        </table>

                                        {/* SP版縦並びレイアウト */}
                                        <div className={sty['env-layout-sp']}>
                                            <div className={sty['env-section-title']}>クライアント</div>
                                            {form.client.map((clientItem, clientIndex) => (
                                                <div key={clientIndex} className={sty['env-item-sp']}>
                                                    <div className={sty['env-item-content']}>
                                                        <div className={sty['env-input-group']}>
                                                            <label className={sty['env-label']}>クライアントタイプ</label>
                                                            {clientItem.client_type.map((type, typeIndex) => (
                                                                <CreatableSelect
                                                                {...register(type.name, validationSchema.select)}
                                                                key={typeIndex}
                                                                value={{ label: type.value, value: "" }}
                                                                isClearable
                                                                placeholder="選択"
                                                                options={envType.clientTypeOptions}
                                                                onChange={(selectedOption) =>
                                                                    handleEnvironmentTypeChange(selectedOption, clientIndex, formIndex, 'client')
                                                                }
                                                                className={sty['form__select-env-type-sp']}
                                                                styles={customStyles}
                                                                theme={(theme) => ({
                                                                    ...theme,
                                                                    borderRadius: 0,
                                                                })}
                                                                noOptionsMessage={() => 'テキストを入力して新規作成'}
                                                                formatCreateLabel={(inputValue) => `クリックで新規作成`}
                                                                aria-label="クライアントタイプ"
                                                                />
                                                            ))}
                                                        </div>
                                                        
                                                        <div className={sty['env-input-group']}>
                                                            <label className={sty['env-label']}>クライアント詳細</label>
                                                            {clientItem.client_con.map((con, conIndex) => (
                                                                <CreatableSelect
                                                                key={conIndex}
                                                                {...register(con.name, validationSchema.client)}
                                                                value={{ label: con.value, value: "" }}
                                                                type="text"
                                                                isClearable
                                                                placeholder="選択"
                                                                options={clientConOptions}
                                                                onChange={(selectedOption) =>
                                                                    handleEnvironmentConChange(selectedOption, clientIndex, formIndex, 'client')
                                                                }
                                                                className={sty['form__select-env-con-sp']}
                                                                styles={customStyles}
                                                                theme={(theme) => ({ ...theme, borderRadius: 0 })}
                                                                noOptionsMessage={() => 'テキストを入力して新規作成'}
                                                                formatCreateLabel={(inputValue) => `クリックで新規作成`}
                                                                aria-label="クライアント詳細"
                                                                />
                                                            ))}
                                                        </div>
                                                        {/* バリデーションエラー */}
                                                        {errors.forms?.[formIndex]?.client_range?.[clientIndex]?.message && (
                                                            <div className="form__validation-msg">
                                                                {errors.forms[formIndex].client_range[clientIndex].message}
                                                            </div>
                                                        )}
                                                        {/* 削除ボタン */}
                                                        {form.client.length > 1 && (
                                                            <div className={sty['env-delete-btn-bottom']}>
                                                                <button
                                                                type="button"
                                                                className={sty['delete-each-btn']}
                                                                onClick={(event) => deleteRow(event, formIndex, 'client', clientIndex)}
                                                                >
                                                                削除
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        </div>
                                      <button type="button" onClick={() => addEnvironmentItem(formIndex, 'client')} className={sty['plus-each-form']} data-testid="clientPls">+</button>
                                  </div>
                              </div>

                              {/* 開発 */}
                              <div className={sty['form__flex']}>
                                  <div className={sty['form__label-flex']}></div>
                                  <div className={sty['form__input']}>
                                      <div>
                                        {/* PC版テーブル */}
                                        <table className={sty['env-table-pc']}>
                                            <tbody>
                                            <tr>
                                                <th colSpan="3" data-category="dev">開発</th>
                                            </tr>

                                            {form.dev.map((devItem, devIndex) => (
                                                <React.Fragment key={devIndex}>
                                                <tr>
                                                    {/* 開発タイプ */}
                                                    <td className={sty['form__td-work-env-type']}>
                                                    {devItem.dev_type.map((type, typeIndex) => (
                                                        <CreatableSelect
                                                        {...register(type.name, validationSchema.select)}
                                                        key={typeIndex}
                                                        value={{ label: type.value, value: "" }}
                                                        isClearable
                                                        placeholder="選択"
                                                        options={envType.devTypeOptions}
                                                        onChange={(selectedOption) =>
                                                            handleEnvironmentTypeChange(selectedOption, devIndex, formIndex, 'dev')
                                                        }
                                                        className={sty['form__select-env-type']}
                                                        styles={customStyles}
                                                        theme={(theme) => ({
                                                            ...theme,
                                                            borderRadius: 0,
                                                        })}
                                                        noOptionsMessage={() => 'テキストを入力して新規作成'}
                                                        formatCreateLabel={(inputValue) => `クリックで"${inputValue}"を新規作成`}
                                                        aria-label="開発タイプ"
                                                        />
                                                    ))}
                                                    </td>

                                                    {/* 開発詳細 */}
                                                    <td className={sty['form__td-work-env-con']}>
                                                    {devItem.dev_con.map((con, conIndex) => (
                                                        <CreatableSelect
                                                        key={conIndex}
                                                        {...register(con.name, validationSchema.dev)}
                                                        value={{ label: con.value, value: "" }}
                                                        type="text"
                                                        isClearable
                                                        placeholder="選択"
                                                        options={devConOptions}
                                                        onChange={(selectedOption) =>
                                                            handleEnvironmentConChange(selectedOption, devIndex, formIndex, 'dev')
                                                        }
                                                        className={sty['form__select-env-con']}
                                                        styles={customStyles}
                                                        theme={(theme) => ({ ...theme, borderRadius: 0 })}
                                                        noOptionsMessage={() => 'テキストを入力して新規作成'}
                                                        formatCreateLabel={(inputValue) => `クリックで"${inputValue}"を新規作成`}
                                                        aria-label="開発詳細"
                                                        />
                                                    ))}
                                                    </td>

                                                    {/* 削除ボタン */}
                                                    {form.dev.length > 1 && (
                                                    <td className={sty['row-delete']}>
                                                        <button
                                                        type="button"
                                                        className={sty['delete-each-btn']}
                                                        onClick={(event) => deleteRow(event, formIndex, 'dev', devIndex)}
                                                        >
                                                        削除
                                                        </button>
                                                    </td>
                                                    )}
                                                </tr>

                                                {/* バリデーションエラー */}
                                                {errors.forms?.[formIndex]?.dev_range?.[devIndex]?.message && (
                                                    <tr>
                                                    <td colSpan="3" className="form__validation-msg">
                                                        {errors.forms[formIndex].dev_range[devIndex].message}
                                                    </td>
                                                    </tr>
                                                )}
                                                </React.Fragment>
                                            ))}
                                            </tbody>
                                        </table>

                                        {/* SP版縦並びレイアウト */}
                                        <div className={sty['env-layout-sp']}>
                                            <div className={sty['env-section-title']}>開発</div>
                                            {form.dev.map((devItem, devIndex) => (
                                                <div key={devIndex} className={sty['env-item-sp']}>
                                                    <div className={sty['env-item-content']}>
                                                        <div className={sty['env-input-group']}>
                                                            <label className={sty['env-label']}>開発タイプ</label>
                                                            {devItem.dev_type.map((type, typeIndex) => (
                                                                <CreatableSelect
                                                                {...register(type.name, validationSchema.select)}
                                                                key={typeIndex}
                                                                value={{ label: type.value, value: "" }}
                                                                isClearable
                                                                placeholder="選択"
                                                                options={envType.devTypeOptions}
                                                                onChange={(selectedOption) =>
                                                                    handleEnvironmentTypeChange(selectedOption, devIndex, formIndex, 'dev')
                                                                }
                                                                className={sty['form__select-env-type-sp']}
                                                                styles={customStyles}
                                                                theme={(theme) => ({
                                                                    ...theme,
                                                                    borderRadius: 0,
                                                                })}
                                                                noOptionsMessage={() => 'テキストを入力して新規作成'}
                                                                formatCreateLabel={(inputValue) => `クリックで新規作成`}
                                                                aria-label="開発タイプ"
                                                                />
                                                            ))}
                                                        </div>
                                                        
                                                        <div className={sty['env-input-group']}>
                                                            <label className={sty['env-label']}>開発詳細</label>
                                                            {devItem.dev_con.map((con, conIndex) => (
                                                                <CreatableSelect
                                                                key={conIndex}
                                                                {...register(con.name, validationSchema.dev)}
                                                                value={{ label: con.value, value: "" }}
                                                                type="text"
                                                                isClearable
                                                                placeholder="選択"
                                                                options={devConOptions}
                                                                onChange={(selectedOption) =>
                                                                    handleEnvironmentConChange(selectedOption, devIndex, formIndex, 'dev')
                                                                }
                                                                className={sty['form__select-env-con-sp']}
                                                                styles={customStyles}
                                                                theme={(theme) => ({ ...theme, borderRadius: 0 })}
                                                                noOptionsMessage={() => 'テキストを入力して新規作成'}
                                                                formatCreateLabel={(inputValue) => `クリックで新規作成`}
                                                                aria-label="開発詳細"
                                                                />
                                                            ))}
                                                        </div>
                                                        {/* バリデーションエラー */}
                                                        {errors.forms?.[formIndex]?.dev_range?.[devIndex]?.message && (
                                                            <div className="form__validation-msg">
                                                                {errors.forms[formIndex].dev_range[devIndex].message}
                                                            </div>
                                                        )}
                                                        {/* 削除ボタン */}
                                                        {form.dev.length > 1 && (
                                                            <div className={sty['env-delete-btn-bottom']}>
                                                                <button
                                                                type="button"
                                                                className={sty['delete-each-btn']}
                                                                onClick={(event) => deleteRow(event, formIndex, 'dev', devIndex)}
                                                                >
                                                                削除
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        </div>
                                      <button type="button" onClick={() => addEnvironmentItem(formIndex, 'dev')} className={sty['plus-each-form']} data-testid="devPls">+</button>
                                  </div>
                              </div>

                              {/* クラウド */}
                              <div className={sty['form__flex']}>
                                  <div className={sty['form__label-flex']}></div>
                                  <div className={sty['form__input']}>
                                      <div>
                                        {/* PC版テーブル */}
                                        <table className={sty['env-table-pc']}>
                                            <tbody>
                                            <tr>
                                                <th colSpan="3" data-category="cloud">クラウド</th>
                                            </tr>

                                            {form.cloud.map((cloudItem, cloudIndex) => (
                                                <React.Fragment key={cloudIndex}>
                                                <tr>
                                                    {/* クラウドタイプ */}
                                                    <td className={sty['form__td-work-env-type']}>
                                                    {cloudItem.cloud_type.map((type, typeIndex) => (
                                                        <CreatableSelect
                                                        {...register(type.name, validationSchema.select)}
                                                        key={typeIndex}
                                                        value={{ label: type.value, value: "" }}
                                                        isClearable
                                                        placeholder="選択"
                                                        options={envType.cloudTypeOptions}
                                                        onChange={(selectedOption) =>
                                                            handleEnvironmentTypeChange(selectedOption, cloudIndex, formIndex, 'cloud')
                                                        }
                                                        className={sty['form__select-env-type']}
                                                        styles={customStyles}
                                                        theme={(theme) => ({
                                                            ...theme,
                                                            borderRadius: 0,
                                                        })}
                                                        noOptionsMessage={() => 'テキストを入力して新規作成'}
                                                        formatCreateLabel={(inputValue) => `クリックで"${inputValue}"を新規作成`}
                                                        aria-label="クラウドタイプ"
                                                        />
                                                    ))}
                                                    </td>

                                                    {/* クラウド詳細 */}
                                                    <td className={sty['form__td-work-env-con']}>
                                                    {cloudItem.cloud_con.map((con, conIndex) => (
                                                        <CreatableSelect
                                                        key={conIndex}
                                                        {...register(con.name, validationSchema.cloud)}
                                                        value={{ label: con.value, value: "" }}
                                                        type="text"
                                                        isClearable
                                                        placeholder="選択"
                                                        options={cloudConOptions}
                                                        onChange={(selectedOption) =>
                                                            handleEnvironmentConChange(selectedOption, cloudIndex, formIndex, 'cloud')
                                                        }
                                                        className={sty['form__select-env-con']}
                                                        styles={customStyles}
                                                        theme={(theme) => ({ ...theme, borderRadius: 0 })}
                                                        noOptionsMessage={() => 'テキストを入力して新規作成'}
                                                        formatCreateLabel={(inputValue) => `クリックで"${inputValue}"を新規作成`}
                                                        aria-label="クラウド詳細"
                                                        />
                                                    ))}
                                                    </td>

                                                    {/* 削除ボタン */}
                                                    {form.cloud.length > 1 && (
                                                    <td className={sty['row-delete']}>
                                                        <button
                                                        type="button"
                                                        className={sty['delete-each-btn']}
                                                        onClick={(event) => deleteRow(event, formIndex, 'cloud', cloudIndex)}
                                                        >
                                                        削除
                                                        </button>
                                                    </td>
                                                    )}
                                                </tr>

                                                {/* バリデーションエラー */}
                                                {errors.forms?.[formIndex]?.cloud_range?.[cloudIndex]?.message && (
                                                    <tr>
                                                    <td colSpan="3" className="form__validation-msg">
                                                        {errors.forms[formIndex].cloud_range[cloudIndex].message}
                                                    </td>
                                                    </tr>
                                                )}
                                                </React.Fragment>
                                            ))}
                                            </tbody>
                                        </table>

                                        {/* SP版縦並びレイアウト */}
                                        <div className={sty['env-layout-sp']}>
                                            <div className={sty['env-section-title']}>クラウド</div>
                                            {form.cloud.map((cloudItem, cloudIndex) => (
                                                <div key={cloudIndex} className={sty['env-item-sp']}>
                                                    <div className={sty['env-item-content']}>
                                                        <div className={sty['env-input-group']}>
                                                            <label className={sty['env-label']}>クラウドタイプ</label>
                                                            {cloudItem.cloud_type.map((type, typeIndex) => (
                                                                <CreatableSelect
                                                                {...register(type.name, validationSchema.select)}
                                                                key={typeIndex}
                                                                value={{ label: type.value, value: "" }}
                                                                isClearable
                                                                placeholder="選択"
                                                                options={envType.cloudTypeOptions}
                                                                onChange={(selectedOption) =>
                                                                    handleEnvironmentTypeChange(selectedOption, cloudIndex, formIndex, 'cloud')
                                                                }
                                                                className={sty['form__select-env-type-sp']}
                                                                styles={customStyles}
                                                                theme={(theme) => ({
                                                                    ...theme,
                                                                    borderRadius: 0,
                                                                })}
                                                                noOptionsMessage={() => 'テキストを入力して新規作成'}
                                                                formatCreateLabel={(inputValue) => `クリックで新規作成`}
                                                                aria-label="クラウドタイプ"
                                                                />
                                                            ))}
                                                        </div>
                                                        
                                                        <div className={sty['env-input-group']}>
                                                            <label className={sty['env-label']}>クラウド詳細</label>
                                                            {cloudItem.cloud_con.map((con, conIndex) => (
                                                                <CreatableSelect
                                                                key={conIndex}
                                                                {...register(con.name, validationSchema.cloud)}
                                                                value={{ label: con.value, value: "" }}
                                                                type="text"
                                                                isClearable
                                                                placeholder="選択"
                                                                options={cloudConOptions}
                                                                onChange={(selectedOption) =>
                                                                    handleEnvironmentConChange(selectedOption, cloudIndex, formIndex, 'cloud')
                                                                }
                                                                className={sty['form__select-env-con-sp']}
                                                                styles={customStyles}
                                                                theme={(theme) => ({ ...theme, borderRadius: 0 })}
                                                                noOptionsMessage={() => 'テキストを入力して新規作成'}
                                                                formatCreateLabel={(inputValue) => `クリックで新規作成`}
                                                                aria-label="クラウド詳細"
                                                                />
                                                            ))}
                                                        </div>
                                                        {/* バリデーションエラー */}
                                                        {errors.forms?.[formIndex]?.cloud_range?.[cloudIndex]?.message && (
                                                            <div className="form__validation-msg">
                                                                {errors.forms[formIndex].cloud_range[cloudIndex].message}
                                                            </div>
                                                        )}
                                                        {/* 削除ボタン */}
                                                        {form.cloud.length > 1 && (
                                                            <div className={sty['env-delete-btn-bottom']}>
                                                                <button
                                                                type="button"
                                                                className={sty['delete-each-btn']}
                                                                onClick={(event) => deleteRow(event, formIndex, 'cloud', cloudIndex)}
                                                                >
                                                                削除
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        </div>
                                      <button type="button" onClick={() => addEnvironmentItem(formIndex, 'cloud')} className={sty['plus-each-form']} data-testid="cloudPls">+</button>
                                  </div>
                              </div>

                              {/* 作業PC */}
                              <div className={sty['form__flex']}>
                                  <div className={sty['form__label-flex']}></div>
                                  <div className={sty['form__input']}>
                                      <div>
                                        {/* PC版テーブル */}
                                        <table className={sty['env-table-pc']}>
                                            <tbody>
                                            <tr>
                                                <th colSpan="3" data-category="pc">作業PC</th>
                                            </tr>

                                            {form.pc.map((pcItem, pcIndex) => (
                                                <React.Fragment key={pcIndex}>
                                                <tr>
                                                    {/* 作業PCタイプ */}
                                                    <td className={sty['form__td-work-env-type']}>
                                                    {pcItem.pc_type.map((type, typeIndex) => (
                                                        <CreatableSelect
                                                        {...register(type.name, validationSchema.select)}
                                                        key={typeIndex}
                                                        value={{ label: type.value, value: "" }}
                                                        isClearable
                                                        placeholder="選択"
                                                        options={envType.pcTypeOptions}
                                                        onChange={(selectedOption) =>
                                                            handleEnvironmentTypeChange(selectedOption, pcIndex, formIndex, 'pc')
                                                        }
                                                        className={sty['form__select-env-type']}
                                                        styles={customStyles}
                                                        theme={(theme) => ({
                                                            ...theme,
                                                            borderRadius: 0,
                                                        })}
                                                        noOptionsMessage={() => 'テキストを入力して新規作成'}
                                                        formatCreateLabel={(inputValue) => `クリックで"${inputValue}"を新規作成`}
                                                        aria-label="作業PCタイプ"
                                                        />
                                                    ))}
                                                    </td>

                                                    {/* 作業PC詳細 */}
                                                    <td className={sty['form__td-work-env-con']}>
                                                    {pcItem.pc_con.map((con, conIndex) => (
                                                        <CreatableSelect
                                                        key={conIndex}
                                                        {...register(con.name, validationSchema.pc)}
                                                        value={{ label: con.value, value: "" }}
                                                        type="text"
                                                        isClearable
                                                        placeholder="選択"
                                                        options={pcConOptions}
                                                        onChange={(selectedOption) =>
                                                            handleEnvironmentConChange(selectedOption, pcIndex, formIndex, 'pc')
                                                        }
                                                        className={sty['form__select-env-con']}
                                                        styles={customStyles}
                                                        theme={(theme) => ({ ...theme, borderRadius: 0 })}
                                                        noOptionsMessage={() => 'テキストを入力して新規作成'}
                                                        formatCreateLabel={(inputValue) => `クリックで"${inputValue}"を新規作成`}
                                                        aria-label="作業PC詳細"
                                                        />
                                                    ))}
                                                    </td>

                                                    {/* 削除ボタン */}
                                                    {form.pc.length > 1 && (
                                                    <td className={sty['row-delete']}>
                                                        <button
                                                        type="button"
                                                        className={sty['delete-each-btn']}
                                                        onClick={(event) => deleteRow(event, formIndex, 'pc', pcIndex)}
                                                        >
                                                        削除
                                                        </button>
                                                    </td>
                                                    )}
                                                </tr>

                                                {/* バリデーションエラー */}
                                                {errors.forms?.[formIndex]?.pc_range?.[pcIndex]?.message && (
                                                    <tr>
                                                    <td colSpan="3" className="form__validation-msg">
                                                        {errors.forms[formIndex].pc_range[pcIndex].message}
                                                    </td>
                                                    </tr>
                                                )}
                                                </React.Fragment>
                                            ))}
                                            </tbody>
                                        </table>

                                        {/* SP版縦並びレイアウト */}
                                        <div className={sty['env-layout-sp']}>
                                            <div className={sty['env-section-title']}>作業PC</div>
                                            {form.pc.map((pcItem, pcIndex) => (
                                                <div key={pcIndex} className={sty['env-item-sp']}>
                                                    <div className={sty['env-item-content']}>
                                                        <div className={sty['env-input-group']}>
                                                            <label className={sty['env-label']}>作業PCタイプ</label>
                                                            {pcItem.pc_type.map((type, typeIndex) => (
                                                                <CreatableSelect
                                                                {...register(type.name, validationSchema.select)}
                                                                key={typeIndex}
                                                                value={{ label: type.value, value: "" }}
                                                                isClearable
                                                                placeholder="選択"
                                                                options={envType.pcTypeOptions}
                                                                onChange={(selectedOption) =>
                                                                    handleEnvironmentTypeChange(selectedOption, pcIndex, formIndex, 'pc')
                                                                }
                                                                className={sty['form__select-env-type-sp']}
                                                                styles={customStyles}
                                                                theme={(theme) => ({
                                                                    ...theme,
                                                                    borderRadius: 0,
                                                                })}
                                                                noOptionsMessage={() => 'テキストを入力して新規作成'}
                                                                formatCreateLabel={(inputValue) => `クリックで新規作成`}
                                                                aria-label="作業PCタイプ"
                                                                />
                                                            ))}
                                                        </div>
                                                        
                                                        <div className={sty['env-input-group']}>
                                                            <label className={sty['env-label']}>作業PC詳細</label>
                                                            {pcItem.pc_con.map((con, conIndex) => (
                                                                <CreatableSelect
                                                                key={conIndex}
                                                                {...register(con.name, validationSchema.pc)}
                                                                value={{ label: con.value, value: "" }}
                                                                type="text"
                                                                isClearable
                                                                placeholder="選択"
                                                                options={pcConOptions}
                                                                onChange={(selectedOption) =>
                                                                    handleEnvironmentConChange(selectedOption, pcIndex, formIndex, 'pc')
                                                                }
                                                                className={sty['form__select-env-con-sp']}
                                                                styles={customStyles}
                                                                theme={(theme) => ({ ...theme, borderRadius: 0 })}
                                                                noOptionsMessage={() => 'テキストを入力して新規作成'}
                                                                formatCreateLabel={(inputValue) => `クリックで新規作成`}
                                                                aria-label="作業PC詳細"
                                                                />
                                                            ))}
                                                        </div>
                                                        {/* バリデーションエラー */}
                                                        {errors.forms?.[formIndex]?.pc_range?.[pcIndex]?.message && (
                                                            <div className="form__validation-msg">
                                                                {errors.forms[formIndex].pc_range[pcIndex].message}
                                                            </div>
                                                        )}
                                                        {/* 削除ボタン */}
                                                        {form.pc.length > 1 && (
                                                            <div className={sty['env-delete-btn-bottom']}>
                                                                <button
                                                                type="button"
                                                                className={sty['delete-each-btn']}
                                                                onClick={(event) => deleteRow(event, formIndex, 'pc', pcIndex)}
                                                                >
                                                                削除
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        </div>
                                      <button type="button" onClick={() => addEnvironmentItem(formIndex, 'pc')} className={sty['plus-each-form']} data-testid="pcPls">+</button>
                                  </div>
                              </div>

                              {/* ネットワーク */}
                              <div className={sty['form__flex']}>
                                  <div className={sty['form__label-flex']}></div>
                                  <div className={sty['form__input']}>
                                      <div>
                                        {/* PC版テーブル */}
                                        <table className={sty['env-table-pc']}>
                                            <tbody>
                                            <tr>
                                                <th colSpan="3" data-category="network">ネットワーク</th>
                                            </tr>

                                            {form.network.map((networkItem, networkIndex) => (
                                                <React.Fragment key={networkIndex}>
                                                <tr>
                                                    {/* ネットワークタイプ */}
                                                    <td className={sty['form__td-work-env-type']}>
                                                    {networkItem.network_type.map((type, typeIndex) => (
                                                        <CreatableSelect
                                                        {...register(type.name, validationSchema.select)}
                                                        key={typeIndex}
                                                        value={{ label: type.value, value: "" }}
                                                        isClearable
                                                        placeholder="選択"
                                                        options={envType.networkTypeOptions}
                                                        onChange={(selectedOption) =>
                                                            handleEnvironmentTypeChange(selectedOption, networkIndex, formIndex, 'network')
                                                        }
                                                        className={sty['form__select-env-type']}
                                                        styles={customStyles}
                                                        theme={(theme) => ({
                                                            ...theme,
                                                            borderRadius: 0,
                                                        })}
                                                        noOptionsMessage={() => 'テキストを入力して新規作成'}
                                                        formatCreateLabel={(inputValue) => `クリックで"${inputValue}"を新規作成`}
                                                        aria-label="ネットワークタイプ"
                                                        />
                                                    ))}
                                                    </td>

                                                    {/* ネットワーク詳細 */}
                                                    <td className={sty['form__td-work-env-con']}>
                                                    {networkItem.network_con.map((con, conIndex) => (
                                                        <CreatableSelect
                                                        key={conIndex}
                                                        {...register(con.name, validationSchema.network)}
                                                        value={{ label: con.value, value: "" }}
                                                        type="text"
                                                        isClearable
                                                        placeholder="選択"
                                                        options={networkConOptions}
                                                        onChange={(selectedOption) =>
                                                            handleEnvironmentConChange(selectedOption, networkIndex, formIndex, 'network')
                                                        }
                                                        className={sty['form__select-env-con']}
                                                        styles={customStyles}
                                                        theme={(theme) => ({ ...theme, borderRadius: 0 })}
                                                        noOptionsMessage={() => 'テキストを入力して新規作成'}
                                                        formatCreateLabel={(inputValue) => `クリックで"${inputValue}"を新規作成`}
                                                        aria-label="ネットワーク詳細"
                                                        />
                                                    ))}
                                                    </td>

                                                    {/* 削除ボタン */}
                                                    {form.network.length > 1 && (
                                                    <td className={sty['row-delete']}>
                                                        <button
                                                        type="button"
                                                        className={sty['delete-each-btn']}
                                                        onClick={(event) => deleteRow(event, formIndex, 'network', networkIndex)}
                                                        >
                                                        削除
                                                        </button>
                                                    </td>
                                                    )}
                                                </tr>

                                                {/* バリデーションエラー */}
                                                {errors.forms?.[formIndex]?.network_range?.[networkIndex]?.message && (
                                                    <tr>
                                                    <td colSpan="3" className="form__validation-msg">
                                                        {errors.forms[formIndex].network_range[networkIndex].message}
                                                    </td>
                                                    </tr>
                                                )}
                                                </React.Fragment>
                                            ))}
                                            </tbody>
                                        </table>

                                        {/* SP版縦並びレイアウト */}
                                        <div className={sty['env-layout-sp']}>
                                            <div className={sty['env-section-title']}>ネットワーク</div>
                                            {form.network.map((networkItem, networkIndex) => (
                                                <div key={networkIndex} className={sty['env-item-sp']}>
                                                    <div className={sty['env-item-content']}>
                                                        <div className={sty['env-input-group']}>
                                                            <label className={sty['env-label']}>ネットワークタイプ</label>
                                                            {networkItem.network_type.map((type, typeIndex) => (
                                                                <CreatableSelect
                                                                {...register(type.name, validationSchema.select)}
                                                                key={typeIndex}
                                                                value={{ label: type.value, value: "" }}
                                                                isClearable
                                                                placeholder="選択"
                                                                options={envType.networkTypeOptions}
                                                                onChange={(selectedOption) =>
                                                                    handleEnvironmentTypeChange(selectedOption, networkIndex, formIndex, 'network')
                                                                }
                                                                className={sty['form__select-env-type-sp']}
                                                                styles={customStyles}
                                                                theme={(theme) => ({
                                                                    ...theme,
                                                                    borderRadius: 0,
                                                                })}
                                                                noOptionsMessage={() => 'テキストを入力して新規作成'}
                                                                formatCreateLabel={(inputValue) => `クリックで新規作成`}
                                                                aria-label="ネットワークタイプ"
                                                                />
                                                            ))}
                                                        </div>
                                                        
                                                        <div className={sty['env-input-group']}>
                                                            <label className={sty['env-label']}>ネットワーク詳細</label>
                                                            {networkItem.network_con.map((con, conIndex) => (
                                                                <CreatableSelect
                                                                key={conIndex}
                                                                {...register(con.name, validationSchema.network)}
                                                                value={{ label: con.value, value: "" }}
                                                                type="text"
                                                                isClearable
                                                                placeholder="選択"
                                                                options={networkConOptions}
                                                                onChange={(selectedOption) =>
                                                                    handleEnvironmentConChange(selectedOption, networkIndex, formIndex, 'network')
                                                                }
                                                                className={sty['form__select-env-con-sp']}
                                                                styles={customStyles}
                                                                theme={(theme) => ({ ...theme, borderRadius: 0 })}
                                                                noOptionsMessage={() => 'テキストを入力して新規作成'}
                                                                formatCreateLabel={(inputValue) => `クリックで新規作成`}
                                                                aria-label="ネットワーク詳細"
                                                                />
                                                            ))}
                                                        </div>
                                                        {/* バリデーションエラー */}
                                                        {errors.forms?.[formIndex]?.network_range?.[networkIndex]?.message && (
                                                            <div className="form__validation-msg">
                                                                {errors.forms[formIndex].network_range[networkIndex].message}
                                                            </div>
                                                        )}
                                                        {/* 削除ボタン */}
                                                        {form.network.length > 1 && (
                                                            <div className={sty['env-delete-btn-bottom']}>
                                                                <button
                                                                type="button"
                                                                className={sty['delete-each-btn']}
                                                                onClick={(event) => deleteRow(event, formIndex, 'network', networkIndex)}
                                                                >
                                                                削除
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        </div>
                                      <button type="button" onClick={() => addEnvironmentItem(formIndex, 'network')} className={sty['plus-each-form']} data-testid="networkPls">+</button>
                                  </div>
                              </div>

                              <div className={sty['primary-btns--project']}>
                                  {forms.length >= 1 && (
                                      <button type="button" onClick={() => deleteForm(form.project_id, form.project_name || `プロジェクト${formIndex + 1}` , formIndex)} className={sty['delete-btn']} data-testid={`form-delete-btn-${formIndex}`}>削除</button>
                                  )}
                                  <button
                                      type="button"
                                      className={`primary-btn ${sty['btn--project']}`}
                                      onClick={() => submitFormHandler(formIndex)}
                                  >保存</button>
                              </div>
                              <div className={sty['form__btn-left']}>
                                  <button type="button" onClick={() => toggleVisibility(formIndex)} className={sty['bottom-show-more-less']}>{visibleStates[formIndex] ? '▲' : '▼'}</button>
                              </div>
                          </div>
                      )}
                  </div>

              </div >
          ))
          }
          {forms.length >= 1 && (
            <div className={sty['form__flex-left']}>
                <button type="button" onClick={addForm} className={sty['plus-pj']} data-testid="add-form-btn">+</button>
            </div>
          )}
        </>
    );
}

function UseIsSPDevice() {
    const [isSPDevice, setIsSPDevice] = useState(false);

    useEffect(() => {
        const checkDevice = () => {
            setIsSPDevice(window.innerWidth < settings.BREAKPOINTS.SP);
        };

        checkDevice(); // 初回チェック

        window.addEventListener("resize", checkDevice);
        return () => window.removeEventListener("resize", checkDevice);
    }, []);

    return isSPDevice;
}

export default Projects;